/*
 * BNO products carousel — PAGE-based, three breakpoints.
 *
 * A slimmed copy of the homepage showcase carousel (assets/bno-home-showcase.js),
 * under its own custom element <bno-products-carousel-scroller> so the showcase is
 * left untouched. It shows a fixed number of product cards per view (3 desktop / 2
 * tablet / 1 mobile) and paginates a whole PAGE at a time, one dot per PAGE.
 *
 *   - Pages by the visible count: page p shows cards [p*visible .. p*visible+visible-1].
 *   - Dots are generated in JS from ceil(cardCount / visible), so the count is
 *     derived and updates automatically when the collection size or breakpoint
 *     changes — nothing is hardcoded.
 *   - Snapping is page-based: only page-start cards carry scroll-snap-align:start,
 *     so a swipe also lands on a page boundary.
 *   - Native horizontal scroll (touch/trackpad) + mouse drag; Left/Right arrows page.
 *   - prefers-reduced-motion: paging jumps instantly.
 *
 * Config via data attributes: data-visible-desktop (default 3), data-visible-tablet
 * (default 2), data-visible-mobile (default 1). The three ranges match the CSS:
 *   mobile  = (max-width: 749px)
 *   tablet  = (min-width: 750px) and (max-width: 989px)
 *   desktop = (min-width: 990px)
 */
class BnoProductsCarouselScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bno-products-carousel__track');
    this.cards = Array.from(this.querySelectorAll('.bno-products-carousel__card'));
    this.dotsWrap = this.querySelector('.bno-products-carousel__dots');

    if (!this.track || this.cards.length === 0) return;

    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mqMobile = window.matchMedia('(max-width: 749px)');
    this.mqTablet = window.matchMedia('(min-width: 750px) and (max-width: 989px)');
    this.visibleDesktop = parseInt(this.dataset.visibleDesktop || '3', 10) || 3;
    this.visibleTablet = parseInt(this.dataset.visibleTablet || '2', 10) || 2;
    this.visibleMobile = parseInt(this.dataset.visibleMobile || '1', 10) || 1;
    this.page = 0;
    this.dots = [];
    /** @type {number | null} */
    this.scrollRaf = null;

    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);

    // Clicking a peeking card pages to that card's page (a drag is not a click —
    // the capture-phase blocker in #setupDrag stops it when the pointer moved).
    this.cards.forEach((card, i) => {
      card.addEventListener('click', () => this.goToPage(Math.floor(i / this.visible())));
    });

    this.#setupDrag();

    this.onBreakpoint = () => this.layout();
    [this.mqMobile, this.mqTablet].forEach((mq) => {
      mq.addEventListener
        ? mq.addEventListener('change', this.onBreakpoint)
        : mq.addListener(this.onBreakpoint);
    });

    // Defensive fallback: rebuild on window resize too, debounced, via the SAME
    // layout() path. Real browsers already handle breakpoint crossings through the
    // matchMedia listeners above; this covers environments that resize the viewport
    // without emitting matchMedia 'change' events (e.g. some headless/preview tools).
    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.layout(), 150);
    };
    window.addEventListener('resize', this.onResize);

    this.layout();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('keydown', this.onKeydown);
    [this.mqMobile, this.mqTablet].forEach((mq) => {
      if (!mq) return;
      mq.removeEventListener
        ? mq.removeEventListener('change', this.onBreakpoint)
        : mq.removeListener(this.onBreakpoint);
    });
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  }

  visible() {
    if (this.mqMobile.matches) return this.visibleMobile;
    if (this.mqTablet.matches) return this.visibleTablet;
    return this.visibleDesktop;
  }

  pageCount() {
    return Math.max(1, Math.ceil(this.cards.length / this.visible()));
  }

  /** Re-run on load and whenever the breakpoint (visible count) changes. */
  layout() {
    const vis = this.visible();
    // Page-based snapping: only every vis-th card (a page start) snaps.
    this.cards.forEach((card, i) => {
      card.style.scrollSnapAlign = i % vis === 0 ? 'start' : 'none';
    });
    this.buildDots();
    this.page = Math.min(this.page, this.pageCount() - 1);
    this.syncActive();
  }

  /** (Re)build the dots to match the current page count. */
  buildDots() {
    if (!this.dotsWrap) return;
    const n = this.pageCount();
    if (this.dots.length !== n) {
      this.dotsWrap.innerHTML = '';
      this.dots = [];
      for (let i = 0; i < n; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bno-products-carousel__dot';
        b.setAttribute('aria-label', `Go to page ${i + 1} of ${n}`);
        b.addEventListener('click', () => this.goToPage(i));
        this.dotsWrap.appendChild(b);
        this.dots.push(b);
      }
    }
    // Hide the dots pill entirely when there is only one page.
    this.dotsWrap.hidden = n <= 1;
  }

  /** The track's snap inset (content-column left), in px. */
  #padStart() {
    return parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
  }

  /** Scroll so page p's first card rests at the content-column left edge. */
  goToPage(p) {
    const pages = this.pageCount();
    p = Math.max(0, Math.min(pages - 1, p));
    this.page = p;
    const idx = Math.min(p * this.visible(), this.cards.length - 1);
    const card = this.cards[idx];
    if (card) {
      const left =
        this.track.scrollLeft +
        (card.getBoundingClientRect().left - this.track.getBoundingClientRect().left) -
        this.#padStart();
      this.track.scrollTo({ left: Math.max(0, left), behavior: this.reduce.matches ? 'auto' : 'smooth' });
    }
    this.syncActive();
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateActiveFromScroll();
    });
  }

  /** Keep the active page in sync with manual swipes (nearest page-start card). */
  updateActiveFromScroll() {
    const vis = this.visible();
    // At (or within 1px of) max scroll, the LAST page is active even if its start
    // card can't left-align: a short final page (e.g. 10 cards / 3 per view leaves 1
    // on the last page) can never reach the content-left edge, so the nearest-card
    // search below would otherwise settle on the second-to-last page.
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (this.track.scrollLeft >= maxScroll - 1) {
      this.page = this.pageCount() - 1;
      this.syncActive();
      return;
    }
    const target = this.track.getBoundingClientRect().left + this.#padStart();
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < this.cards.length; i += vis) {
      const d = Math.abs(this.cards[i].getBoundingClientRect().left - target);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    this.page = best / vis;
    this.syncActive();
  }

  syncActive() {
    this.dots.forEach((dot, i) => {
      const active = i === this.page;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goToPage(this.page + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goToPage(this.page - 1);
    }
  }

  /** Mouse drag-to-scroll. Touch keeps native scrolling. */
  #setupDrag() {
    const track = this.track;
    let down = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let captured = false;
    let pointerId = null;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      captured = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        track.style.scrollSnapType = 'none';
        // Flip the cursor to "grabbing" for the duration of the drag (CSS reads
        // .is-dragging). Only reached for mouse drags (touch returns early above).
        track.classList.add('is-dragging');
        try {
          track.setPointerCapture(pointerId);
          captured = true;
        } catch (_) {
          /* ignore */
        }
      }
      if (moved) track.scrollLeft = startScroll - dx;
    });

    const end = () => {
      if (!down) return;
      down = false;
      if (moved) {
        track.style.scrollSnapType = '';
        track.classList.remove('is-dragging');
        if (captured) {
          try {
            track.releasePointerCapture(pointerId);
          } catch (_) {
            /* ignore */
          }
        }
        requestAnimationFrame(() => this.updateActiveFromScroll());
      }
    };

    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }
}

if (!customElements.get('bno-products-carousel-scroller')) {
  customElements.define('bno-products-carousel-scroller', BnoProductsCarouselScroller);
}
